import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  createStyles,
  makeStyles,
  ThemeProvider
} from '@material-ui/core/styles';
import createMuiTheme from '@material-ui/core/styles/createTheme';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import createActivityDetector from 'activity-detector';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import {connect, useDispatch} from 'react-redux';
import {Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import locale from '../locale/Localization';
import {checkRolePermissions} from '../permissions/permissions';
import {SET_ACTIVE, START_SESSION_POLLING} from '../store/action-types';
import ArchiveApp from './ArchiveApp';
import EmployeeList from './EmployeeList';
import Footer from './Footer';
import HomeApp from './HomeApp';
import Navigation from './Navigation';
import News from './News';
import NotariesApp from './NotariesApp';
import NotaryApplications from './NotaryApplications';
import NotaryRegistration from './NotaryRegistration';
import Notifications from './Notifications';
import Signer from './Signer';
import UserProfile from './UserProfile';

const theme = createMuiTheme({
  typography: {
    fontFamily: 'Helvetica Neue'
  }
});

const mapStateToProps = (state) => {
  return {
    user: state.user,
    roles: state.roles,
    archives: state.archives,
    notifications: state.notifications
  };
};

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {flexGrow: 1},
    menuButton: {marginRight: theme.spacing(2)},
    title: {flexGrow: 1, marginLeft: '-1em'},
    header: {backgroundColor: '#D60937'},
    body: {minHeight: '60vh'}
  })
);

/**
 * Creates the portal basic layout.
 * @return {React.Fragment} with portal navigation
 */
// eslint-disable-next-line max-lines-per-function, require-jsdoc
const ConnectedPortal = ({user, roles, archives, notifications}) => {
  const dispatch = useDispatch();
  const activityDetector = createActivityDetector();
  activityDetector.on('active', () => {
    dispatch({type: SET_ACTIVE, payload: true});
  });
  activityDetector.on('idle', () => {
    dispatch({type: SET_ACTIVE, payload: false});
  });

  useEffect(() => {
    if (user.firstNames !== 'Guest') {
      dispatch({type: START_SESSION_POLLING});
    }
  });

  const displayApplications = () => {
    return (
      checkRolePermissions(roles, 'VIEW', 'Applications') ||
      checkRolePermissions(roles, 'VIEW', 'Person Management')
    );
  };

  const renderRoutes = () => {
    return (
      <Switch>
        <Route path='/news'>
          <News />
        </Route>
        {checkRolePermissions(roles, 'VIEW', 'Home') && (
          <Route path='/home'>
            <HomeApp />
          </Route>
        )}
        {checkRolePermissions(roles, 'VIEW', 'Employee Management') && (
          <Route path='/employee-management'>
            <EmployeeList />
          </Route>
        )}
        {Object.keys(archives).length > 0 && (
          <Route path='/archive'>
            <ArchiveApp />
          </Route>
        )}
        {checkRolePermissions(roles, 'VIEW', 'UserProfile') && (
          <Route path='/profile'>
            <UserProfile />
          </Route>
        )}
        {checkRolePermissions(roles, 'VIEW', 'UserProfile') && (
          <Route path='/signer'>
            <Signer />
          </Route>
        )}
        <Route path='/registration/:uuid'>
          <NotaryRegistration />
        </Route>
        {displayApplications() && (
          <Route path='/applications'>
            <NotaryApplications />
          </Route>
        )}
        <Route path='/'>
          <NotariesApp />
        </Route>
      </Switch>
    );
  };

  const classes = useStyles();

  return (
    <ThemeProvider theme={theme}>
      <React.Fragment>
        <CssBaseline />
        <Router>
          <AppBar position='static' className={classes.header}>
            <div id='wrapperHeader' className='header-bar'>
              <div>
                <div id='header' className='header-content'>
                  <div className='header-centered'></div>
                </div>
              </div>
            </div>
            <Container maxWidth='md'>
              <Typography component='div'>
                <Toolbar>
                  <Typography variant='h6' className={classes.title}>
                    {locale.pageTitle}
                  </Typography>
                  <Navigation />
                </Toolbar>
              </Typography>
            </Container>
          </AppBar>
          <Container maxWidth='md'>
            <Notifications notifications={notifications} />
            <Typography component='div' className={classes.body}>
              <Box m={4}>{renderRoutes}</Box>
            </Typography>
          </Container>
        </Router>
        <Footer />
      </React.Fragment>
    </ThemeProvider>
  );
};

ConnectedPortal.propTypes = {
  archives: PropTypes.array.isRequired,
  notifications: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired
};

const Portal = connect(mapStateToProps, null)(ConnectedPortal);
export default Portal;
